/*
 * Frete e prazo de entrega por CEP.
 *
 * - assets/olimpo-prazos.json: prazo oficial dos Correios por prefixo de CEP
 *   ({ chave } dígitos, 5 na versão atual). { f: [[prefixo, pac, sedex], ...] } em ordem crescente; cada
 *   faixa vale até o próximo prefixo; 0 = prefixo sem prazo nos Correios.
 * - assets/olimpo-fretes.json: fretes fixos da loja por zona (preço, serviço,
 *   pedido mínimo) e a estimativa de prazo da região, usada quando o CEP não
 *   está na tabela de prazos.
 *
 * Os prazos são trânsito; aqui somamos o processamento da loja
 * (até 1 dia útil, 2 fora do horário).
 */
if (!customElements.get('olimpo-prazo-cep')) {
  const CHAVE_CEP = 'olimpo-prazo-cep';
  const cache = new Map();
  const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

  // Faixas de CEP por estado (5 primeiros dígitos). 78900-78999 é a faixa antiga
  // de Rondônia: hoje não tem CEPs, mas recebe a estimativa da região.
  const UFS = [
    ['SP', 1000, 19999], ['RJ', 20000, 28999], ['ES', 29000, 29999], ['MG', 30000, 39999],
    ['BA', 40000, 48999], ['SE', 49000, 49999], ['PE', 50000, 56999], ['AL', 57000, 57999],
    ['PB', 58000, 58999], ['RN', 59000, 59999], ['CE', 60000, 63999], ['PI', 64000, 64999],
    ['MA', 65000, 65999], ['PA', 66000, 68899], ['AP', 68900, 68999], ['AM', 69000, 69299],
    ['RR', 69300, 69399], ['AM', 69400, 69899], ['AC', 69900, 69999], ['DF', 70000, 72799],
    ['GO', 72800, 72999], ['DF', 73000, 73699], ['GO', 73700, 76799], ['RO', 76800, 76999],
    ['TO', 77000, 77999], ['MT', 78000, 78899], ['RO', 78900, 78999], ['MS', 79000, 79999],
    ['PR', 80000, 87999],
    ['SC', 88000, 89999], ['RS', 90000, 99999],
  ];
  const ufDoCep = (cep) => {
    const n = Number(cep.slice(0, 5));
    const faixa = UFS.find(([, ini, fim]) => n >= ini && n <= fim);
    return faixa ? faixa[0] : null;
  };

  const carregar = (url) => {
    if (!cache.has(url)) {
      cache.set(url, fetch(url).then((r) => {
        if (!r.ok) throw new Error('arquivo indisponível');
        return r.json();
      }).catch((e) => { cache.delete(url); throw e; }));
    }
    return cache.get(url);
  };

  // Busca binária: última faixa com prefixo <= o prefixo do CEP.
  const buscarFaixa = (faixas, prefixo) => {
    let ini = 0;
    let fim = faixas.length - 1;
    let achou = null;
    while (ini <= fim) {
      const meio = (ini + fim) >> 1;
      if (faixas[meio][0] <= prefixo) { achou = faixas[meio]; ini = meio + 1; } else { fim = meio - 1; }
    }
    return achou;
  };

  const lerCepSalvo = () => {
    try { return localStorage.getItem(CHAVE_CEP) || ''; } catch (e) { return ''; }
  };
  const salvarCep = (cep) => {
    try { localStorage.setItem(CHAVE_CEP, cep); } catch (e) { /* sem armazenamento */ }
  };
  const mascarar = (valor) => {
    const d = valor.replace(/\D/g, '').slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  };

  class OlimpoPrazoCep extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.olimpo-prazo__input');
      this.resultado = this.querySelector('.olimpo-prazo__resultado');
      this.form = this.querySelector('.olimpo-prazo__form');
      this.minProc = Number(this.dataset.processamentoMin) || 1;
      this.maxProc = Number(this.dataset.processamentoMax) || 2;
      // Total do carrinho em reais; vazio na página do produto.
      this.totalCarrinho = this.dataset.totalCarrinho ? Number(this.dataset.totalCarrinho) / 100 : null;

      this.input.addEventListener('input', () => { this.input.value = mascarar(this.input.value); });
      this.form.addEventListener('submit', (ev) => { ev.preventDefault(); this.calcular(); });

      // O carrinho re-renderiza o drawer a cada alteração: recalcula com o CEP já informado.
      const salvo = lerCepSalvo();
      if (salvo) { this.input.value = mascarar(salvo); this.calcular(); }
    }

    async calcular() {
      const cep = this.input.value.replace(/\D/g, '');
      if (cep.length !== 8) { this.mostrarErro('Digite um CEP com 8 números.'); return; }
      const uf = ufDoCep(cep);
      if (!uf) { this.mostrarErro('CEP inválido. Confira o número digitado.'); return; }
      salvarCep(cep);
      try {
        const [prazos, fretes] = await Promise.all([carregar(this.dataset.tabela), carregar(this.dataset.fretes)]);
        const zona = fretes.zonas.find((z) => z.ufs.includes(uf));
        if (!zona) { this.mostrarErro('Ainda não entregamos nessa região.'); return; }
        const faixa = buscarFaixa(prazos.f, cep.slice(0, prazos.chave || 4));
        const exato = faixa && faixa[1] ? { pac: faixa[1], sedex: faixa[2] || faixa[1] } : null;
        this.mostrar(zona, exato);
      } catch (e) {
        this.mostrarErro('Não foi possível calcular agora. Tente novamente.');
      }
    }

    textoPrazo(servico, exato, zona) {
      if (exato) return `${exato[servico] + this.minProc} a ${exato[servico] + this.maxProc} dias úteis`;
      const [min, max] = (zona.prazoRegiao && zona.prazoRegiao[servico]) || [];
      return min ? `${min + this.minProc} a ${max + this.maxProc} dias úteis` : 'Prazo informado no checkout';
    }

    textoPreco(frete) {
      if (!frete.minimoPedido) return frete.preco > 0 ? moeda.format(frete.preco) : 'Grátis';
      const minimo = moeda.format(frete.minimoPedido);
      if (this.totalCarrinho === null) return `Grátis acima de ${minimo}`;
      if (this.totalCarrinho >= frete.minimoPedido) return 'Grátis';
      return `Grátis acima de ${minimo} (faltam ${moeda.format(frete.minimoPedido - this.totalCarrinho)})`;
    }

    mostrar(zona, exato) {
      const lista = document.createElement('ul');
      lista.className = 'olimpo-prazo__lista';
      for (const frete of zona.fretes) {
        const li = document.createElement('li');
        const servico = document.createElement('span');
        servico.className = 'olimpo-prazo__servico';
        const nome = document.createElement('strong');
        nome.textContent = frete.nome;
        const prazo = document.createElement('small');
        prazo.textContent = this.textoPrazo(frete.servico, exato, zona);
        servico.append(nome, prazo);
        const preco = document.createElement('span');
        preco.className = 'olimpo-prazo__preco';
        preco.textContent = this.textoPreco(frete);
        li.append(servico, preco);
        lista.append(li);
      }
      const itens = [lista];
      if (!exato) {
        const nota = document.createElement('p');
        nota.className = 'olimpo-prazo__nota';
        nota.textContent = 'Prazo estimado para a sua região.';
        itens.push(nota);
      }
      this.resultado.replaceChildren(...itens);
    }

    mostrarErro(texto) {
      const p = document.createElement('p');
      p.className = 'olimpo-prazo__erro';
      p.textContent = texto;
      this.resultado.replaceChildren(p);
    }
  }

  customElements.define('olimpo-prazo-cep', OlimpoPrazoCep);
}
